// Analytics: Meta Pixel + GA4 + Google Ads conversion tag (issue #203, epic #917)
//
// Env-gated, lazy-loaded browser tracking for the public site. Nothing loads
// unless the corresponding env var is set AND the page is on a production host
// (tracking-host.ts, #1303): Vercel previews are built with the production env,
// so the env check alone let them fire the live pixel. The admin app never
// ships a tracker.
//
// Design notes:
// - The Meta Pixel base snippet here does NOT auto-fire PageView; the router
//   listener is the single PageView/page_view source (see App.tsx).
// - GA4 is configured with `send_page_view: false` for the same reason.
// - Every exported function is internally guarded so it can NEVER throw into a
//   caller (e.g. a form-submit mutation). Failures degrade to a console.warn.
//   The consent gates (#741) sit OUTSIDE the try, for parity with marketing's
//   copy — safe because consent.ts wraps both its cookie read and its parse in
//   try/catch, so the predicate cannot throw.
// - `Lead` events carry a shared `eventID` so the server-side Conversions API
//   (supabase/functions/meta-capi) can dedupe the browser/server pair.
// - `sendPixelBeacon` (#1368) sends a pixel event from a page the trackers skip
//   (`/quote/*`), through the image pixel with an explicit sanitized URL.

import { analyticsAllowed } from "./consent";
import { trackingHostAllowed } from "./tracking-host";

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
// Google Ads conversion tag (#917). ADS_LEAD_SEND_TO is "AW-<id>/<label>" of the
// "Tristano – Form Lead" action — the one conversion Search bidding optimizes
// to, previously fired only by the GHL funnel's thank-you page. Moving ads onto
// the SPA without firing it here would blind the bidder.
const ADS_LEAD_SEND_TO = import.meta.env.VITE_GOOGLE_ADS_LEAD_SEND_TO as string | undefined;
const ADS_ID = ADS_LEAD_SEND_TO?.split("/")[0] || undefined;

/** Cookie name for the first-touch fbclid captured at landing (#486). */
export const FBCLID_STORAGE_KEY = "tc_fbclid";

type FbqArgs = [string, string, Record<string, unknown>?, { eventID?: string }?];
type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    fbq?: ((...args: FbqArgs) => void) & {
      callMethod?: (...args: FbqArgs) => void;
      queue?: FbqArgs[];
      loaded?: boolean;
      version?: string;
      push?: (...args: FbqArgs) => void;
    };
    _fbq?: Window["fbq"];
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

let initialized = false;

function injectScript(src: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function initPixel(): void {
  if (!PIXEL_ID || window.fbq) return;
  // Standard Meta bootstrap stub — queues calls until fbevents.js loads.
  // Intentionally omits the trailing `fbq('track','PageView')` so the router
  // listener owns PageView.
  const fbq = function (...args: FbqArgs) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue!.push(args);
  } as NonNullable<Window["fbq"]>;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;
  injectScript("https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", PIXEL_ID);
}

function initGtag(): void {
  if ((!GA4_ID && !ADS_ID) || window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  // gtag.js only processes `arguments` objects from the dataLayer. A plain
  // array (the old `(...args) => push(args)`) is silently ignored, so from #203
  // until #917 GA4 on the SPA never sent a single hit. Keep the canonical form.
  const gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as NonNullable<Window["gtag"]>;
  window.gtag = gtag;
  // Cross-domain measurement (#310): the linker `domains` MUST be registered via
  // `set` BEFORE the `js`/`config` commands — gtag.js ignores `domains` passed
  // inside `config`. This decorates outbound links to the sister domain with the
  // `_gl` client-id param so GA stitches the marketing <-> app.tristanoscatering.ca
  // sessions into one funnel (otherwise marketing-sourced wizard conversions log
  // as direct / self-referral). GA matches by hostname substring, so the apex
  // covers www/staging/app subdomains; the app host is listed explicitly.
  gtag("set", "linker", { domains: ["tristanoscatering.ca", "app.tristanoscatering.ca"] });
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID || ADS_ID}`);
  gtag("js", new Date());
  // Listener owns page_view, so suppress the automatic config hit; accept the
  // inbound `_gl` param so this domain joins sessions handed off from the other.
  if (GA4_ID) gtag("config", GA4_ID, { send_page_view: false, linker: { accept_incoming: true } });
  // Configuring the AW id turns on the conversion linker: it stores the landing
  // gclid in the _gcl_aw cookie so the later lead conversion is attributed to
  // the ad click. No page_view — this destination only needs the conversion.
  if (ADS_ID) gtag("config", ADS_ID, { send_page_view: false });
}

/**
 * Lazily injects the pixel + GA4 scripts. Safe to call repeatedly (idempotent)
 * and safe to call when env is unset (no-op). Never throws.
 */
export function initAnalytics(): void {
  if (initialized) return;
  if (!trackingHostAllowed()) return; // previews never load a tracker (#1303)
  if (!analyticsAllowed()) return; // opt-out: skip injection entirely if the visitor declined (#741)
  try {
    initPixel();
    initGtag();
    initialized = true;
  } catch (err) {
    console.warn("[analytics] init failed", err);
  }
}

/** True when at least one tracker is configured via env and this is a production host (#1303). */
export function analyticsEnabled(): boolean {
  return Boolean(PIXEL_ID || GA4_ID || ADS_ID) && trackingHostAllowed();
}

/**
 * Hard-stop trackers when a visitor declines mid-session (#486): flip GA4's
 * global kill switch and revoke Meta pixel consent. Already-injected scripts then
 * stop sending; the analyticsAllowed() gate keeps future calls from firing too (#741).
 *
 * Also purges any first-touch attribution cookies already written (#486):
 * capture runs in main.tsx module-level code, before any consent banner can
 * possibly render, so a first-time visitor's cookies exist before Decline
 * does — the analyticsAllowed() gate alone only protects a *returning*
 * decliner. clearAttribution() is what makes Decline mean something for the
 * common case.
 */
export function disableAnalytics(): void {
  // Imported inline to avoid circular dependency (attribution imports from analytics)
  import("./attribution").then(({ clearAttribution }) => {
    clearAttribution();
  });
  try {
    if (GA4_ID) (window as unknown as Record<string, boolean>)[`ga-disable-${GA4_ID}`] = true;
    if (ADS_ID) (window as unknown as Record<string, boolean>)[`ga-disable-${ADS_ID}`] = true;
    if (window.fbq) window.fbq("consent", "revoke");
  } catch (err) {
    console.warn("[analytics] disable failed", err);
  }
}

/** Fire a PageView (Meta) + page_view (GA4) for an already-sanitized path. */
export function trackPageView(path: string): void {
  if (!analyticsAllowed()) return; // #741
  try {
    if (window.fbq) window.fbq("track", "PageView");
    if (window.gtag && GA4_ID) {
      // GA4 prefers page_location + page_title over page_path alone (#310).
      // page_location is built from the SANITIZED path (not window.location.href)
      // so no UUIDs/tokens/query PII reach GA4. page_title is best-effort — on a
      // fast client nav it may lag a page if the route sets <title> in a later effect.
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.origin + path,
        page_title: document.title,
      });
    }
  } catch (err) {
    console.warn("[analytics] trackPageView failed", err);
  }
}

/**
 * Fire a deduplicated Lead (Meta) + generate_lead (GA4) + the Google Ads
 * "Form Lead" conversion (#917, transaction_id = eventId so a re-fire of the
 * same lead can't count twice). Pass `value` (+ optional
 * `currency`, default CAD) so Meta/GA4 can bid to conversion value rather than
 * treating every lead the same (#278) — e.g. the /order cart subtotal, or an
 * estimate from guest count. Omitting value keeps the legacy call shape.
 * `contentCategory` names the lead's funnel on the Meta Lead only (#925), so
 * Meta can run one custom conversion per funnel. Pass it already normalized
 * (lead-tracking.ts does), so it matches the server leg.
 */
export function trackLead(opts: {
  eventId: string;
  value?: number;
  currency?: string;
  contentCategory?: string;
}): void {
  if (!analyticsAllowed()) return; // #741
  try {
    const valued = opts.value != null && opts.value > 0;
    const custom = {
      ...(valued ? { value: opts.value, currency: opts.currency ?? "CAD" } : {}),
      ...(opts.contentCategory ? { content_category: opts.contentCategory } : {}),
    };
    if (window.fbq) window.fbq("track", "Lead", custom, { eventID: opts.eventId });
    if (window.gtag && GA4_ID) {
      window.gtag("event", "generate_lead", {
        event_id: opts.eventId,
        ...(valued ? { value: opts.value, currency: opts.currency ?? "CAD" } : {}),
      });
    }
    if (window.gtag && ADS_LEAD_SEND_TO) {
      window.gtag("event", "conversion", {
        send_to: ADS_LEAD_SEND_TO,
        transaction_id: opts.eventId,
        ...(valued ? { value: opts.value, currency: opts.currency ?? "CAD" } : {}),
      });
    }
  } catch (err) {
    console.warn("[analytics] trackLead failed", err);
  }
}

/** A path safe to report: the quote UUID (the public page's only credential) becomes `:id`. */
export function sanitizeTrackedPath(pathname: string): string {
  return pathname.replace(/^\/quote\/[^/]+/, "/quote/:id");
}

/**
 * Send one Meta event through the image pixel (`facebook.com/tr`) rather than
 * `fbq` (#1368). fbevents.js attaches `dl=document.location.href` to every hit,
 * and on `/quote/:id` that URL holds the quote UUID (which is why App.tsx never
 * loads the pixel there). Here `dl` is the origin plus the sanitized path, with
 * no query or hash, and no referrer is sent. `eventId` rides as `eid`, which
 * Meta dedupes against the CAPI event's `event_id`. `value` is in CAD. Gated
 * like every other tracker call; never throws.
 */
export function sendPixelBeacon(event: string, eventId: string, value?: number): void {
  if (!PIXEL_ID || !analyticsAllowed() || !trackingHostAllowed()) return;
  try {
    const params = new URLSearchParams({
      id: PIXEL_ID,
      ev: event,
      eid: eventId,
      dl: window.location.origin + sanitizeTrackedPath(window.location.pathname),
      noscript: "1",
    });
    if (value != null && value > 0) {
      params.set("cd[value]", String(value));
      params.set("cd[currency]", "CAD");
    }
    const fbp = getFbp();
    if (fbp) params.set("fbp", fbp);
    const fbc = getFbc();
    if (fbc) params.set("fbc", fbc);
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    img.src = `https://www.facebook.com/tr?${params.toString()}`;
  } catch (err) {
    console.warn("[analytics] sendPixelBeacon failed", err);
  }
}

/**
 * Conservative per-guest dollar figure used ONLY to attach an estimated `value`
 * to a wizard Lead for Meta/GA4 value-based bidding (#278). This is an internal
 * ad-optimization signal — NOT a quote, NOT shown to the customer, and kept
 * deliberately low so we never overstate conversion value to the ad platforms.
 * Real pricing comes from menu/package line items at quote time. Tune freely;
 * the wiring (not the exact number) is what makes value-based bidding work.
 */
const ESTIMATED_VALUE_PER_GUEST_CAD = 30;

/**
 * Clamp the guest count used for the estimate so a typo or a spammed wizard
 * submission (anon INSERT is open) can't push a wild conversion value into the
 * ad platforms. 1,000 covers virtually every real event.
 */
const MAX_GUESTS_FOR_ESTIMATE = 1000;

/**
 * Estimate a Lead's dollar value from its guest count for value-based bidding
 * (#278). Returns `undefined` for a missing / non-positive / non-finite count
 * so callers fall back to the legacy value-less Lead. The wizard captures no
 * cart, so this stands in for the /order subtotal that drives value bidding there.
 */
export function estimateLeadValue(guestCount?: number | null): number | undefined {
  if (typeof guestCount !== "number" || !Number.isFinite(guestCount) || guestCount <= 0) {
    return undefined;
  }
  const guests = Math.min(Math.round(guestCount), MAX_GUESTS_FOR_ESTIMATE);
  return guests * ESTIMATED_VALUE_PER_GUEST_CAD;
}

/** Read a cookie value by name; null when absent or storage throws. */
function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/** Meta `_fbp` browser-id cookie, when present. */
export function getFbp(): string | null {
  return readCookie("_fbp");
}

/**
 * Meta `fbc` click-id for CAPI match quality. Resolution order:
 *   1. `_fbc` cookie (set by the pixel when it loads with ?fbclid)
 *   2. first-touch fbclid persisted at landing -> `fb.1.<ts>.<fbclid>`
 *   3. fbclid in the current URL -> `fb.1.<ts>.<fbclid>`
 * Returns null when no signal is available.
 */
export function getFbc(): string | null {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;

  let fbclid: string | null = readCookie(FBCLID_STORAGE_KEY);
  if (!fbclid) {
    try {
      fbclid = new URLSearchParams(window.location.search).get("fbclid");
    } catch {
      fbclid = null;
    }
  }
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null;
}
